import { useEffect, useId, useRef, useState } from 'react';

/**
 * A column header with an explanation on click.
 *
 * The results table is full of telecom terms — PDD, Q.850, DIALSTATUS — that
 * mean nothing to someone outside the field. Instead of explaining them in
 * documentation nobody reads, the explanation sits where the term appears.
 *
 * Click, not hover: on touch there is no hover, and the texts are too long for
 * a tooltip that disappears when you move the mouse.
 */
export interface ColumnHelp {
  title: string;
  body: string;
  /** The possible values, when the column has a closed vocabulary. */
  values?: { key: string; meaning: string }[];
}

// Explicit `| undefined`: with exactOptionalPropertyTypes a dictionary lookup
// returns `ColumnHelp | undefined`, and `help?:` alone does not accept that.
export default function HelpHeader(
  { label, help }: { label: string; help?: ColumnHelp | undefined },
) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLTableCellElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!help) return <th>{label}</th>;

  return (
    <th ref={ref} className="th-help">
      <button type="button" className="th-btn" aria-expanded={open} aria-controls={id}
              onClick={() => setOpen(!open)}>
        {label}<span className="th-mark" aria-hidden="true">?</span>
      </button>

      {open && (
        <div className="popover" id={id} role="dialog" aria-label={help.title}>
          <div className="popover-title">{help.title}</div>
          <p className="popover-body">{help.body}</p>
          {help.values && (
            <dl className="popover-values">
              {help.values.map((v) => (
                <div key={v.key}>
                  <dt>{v.key}</dt><dd>{v.meaning}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </th>
  );
}
